import { z } from 'zod';

import { DEFAULT_AUTO_APPLY_CLASSIFICATION_THRESHOLD } from './ai-constants.js';
import {
  DEFAULT_VIOLATIONS_QUIET_HOURS,
  VIOLATION_SEVERITIES,
  VIOLATION_SOURCE_DESCRIPTORS,
  VIOLATION_SOURCE_IDS,
  type ViolationSeverity,
  type ViolationSourceId,
} from './violation-constants.js';
import { WORK_ORDER_SEVERITIES, type WorkOrderSeverity } from './work-order-constants.js';

/**
 * Phase 2 introduces the per-org settings document. The row exists in the
 * `org_settings` table as a single jsonb payload (`settings`) — flat
 * column promotion can come later if/when multiple modules need indexed
 * settings lookups. For now, `settings` is the bag.
 *
 * Only the work-order section is defined here; later phases (vendors,
 * compliance, AI, notifications) extend this schema by adding sibling
 * keys. Unknown keys are stripped at write time so a forward-compatible
 * UI can't poison the document.
 */

export const orgWorkOrderSettingsSchema = z.object({
  workOrderPrefix: z.string().trim().min(1).max(8).default('WO'),
  useBuildingPrefix: z.boolean().default(false),
  defaultSeverity: z.enum(WORK_ORDER_SEVERITIES).default('medium'),
  autoTriageEnabled: z.boolean().default(false),
  requirePhotoOnCompletion: z.boolean().default(true),
  allowSelfAssignment: z.boolean().default(true),
});
export type OrgWorkOrderSettings = z.infer<typeof orgWorkOrderSettingsSchema>;

export const DEFAULT_ORG_WORK_ORDER_SETTINGS: OrgWorkOrderSettings = {
  workOrderPrefix: 'WO',
  useBuildingPrefix: false,
  defaultSeverity: 'medium' as WorkOrderSeverity,
  autoTriageEnabled: false,
  requirePhotoOnCompletion: true,
  allowSelfAssignment: true,
};

// ---------- Violations (Phase 7) ----------

/**
 * Per-source poll interval override (milliseconds). Keys are the
 * canonical source IDs; values must be positive integers. Unknown keys
 * are stripped on parse so a forward-compatible UI can't poison the
 * document. The dispatcher falls back to
 * `VIOLATION_SOURCE_DESCRIPTORS[id].defaultPollIntervalMs` when no
 * override is present.
 */
const pollingIntervalOverridesSchema = z
  .record(z.enum(VIOLATION_SOURCE_IDS), z.number().int().min(60_000).max(7 * 24 * 60 * 60 * 1000))
  .default({});

const quietHoursSchema = z
  .object({
    enabled: z.boolean().default(true),
    startHour: z.number().int().min(0).max(23).default(DEFAULT_VIOLATIONS_QUIET_HOURS.startHour),
    endHour: z.number().int().min(0).max(23).default(DEFAULT_VIOLATIONS_QUIET_HOURS.endHour),
    timezone: z.string().min(1).max(64).default(DEFAULT_VIOLATIONS_QUIET_HOURS.timezone),
  })
  .default({
    enabled: true,
    startHour: DEFAULT_VIOLATIONS_QUIET_HOURS.startHour,
    endHour: DEFAULT_VIOLATIONS_QUIET_HOURS.endHour,
    timezone: DEFAULT_VIOLATIONS_QUIET_HOURS.timezone,
  });

export const orgViolationSettingsSchema = z.object({
  /** When true, the worker auto-creates a WO from any violation whose
   *  severity meets or exceeds `autoCreateWorkOrdersThreshold`.
   *  Off by default — operators convert violations to work orders
   *  manually from the violation detail page. */
  autoCreateWorkOrders: z.boolean().default(false),
  autoCreateWorkOrdersThreshold: z.enum(VIOLATION_SEVERITIES).default('high'),
  pollingIntervalOverrides: pollingIntervalOverridesSchema,
  quietHours: quietHoursSchema,
});
export type OrgViolationSettings = z.infer<typeof orgViolationSettingsSchema>;

export const DEFAULT_ORG_VIOLATION_SETTINGS: OrgViolationSettings = {
  autoCreateWorkOrders: false,
  autoCreateWorkOrdersThreshold: 'high' as ViolationSeverity,
  pollingIntervalOverrides: {} as Record<ViolationSourceId, number>,
  quietHours: {
    enabled: true,
    startHour: DEFAULT_VIOLATIONS_QUIET_HOURS.startHour,
    endHour: DEFAULT_VIOLATIONS_QUIET_HOURS.endHour,
    timezone: DEFAULT_VIOLATIONS_QUIET_HOURS.timezone,
  },
};

/** Resolve the effective interval for a given source on a given org. */
export function resolvePollIntervalMs(
  settings: OrgViolationSettings,
  sourceId: ViolationSourceId,
): number {
  const override = settings.pollingIntervalOverrides[sourceId];
  if (override && override > 0) return override;
  return VIOLATION_SOURCE_DESCRIPTORS[sourceId].defaultPollIntervalMs;
}

// ---------- AI layer (Phase 8) ----------

/**
 * Per-feature toggles exposed to the org admin. Each one gates the
 * corresponding worker subscription / API endpoint; the underlying
 * non-AI feature still works when these are false (the spec's "fall
 * back gracefully" rule). Mirrors `ai_quotas.features_enabled` jsonb.
 */
const aiFeatureTogglesSchema = z.object({
  emailParse: z.boolean().default(true),
  woClassify: z.boolean().default(true),
  violationSummarize: z.boolean().default(true),
  voiceToWo: z.boolean().default(true),
});

export const orgAiSettingsSchema = z.object({
  /** Master kill-switch. When false every AI call short-circuits to the
   *  non-AI path; per-feature toggles below are ignored. */
  enabled: z.boolean().default(true),
  features: aiFeatureTogglesSchema.default({
    emailParse: true,
    woClassify: true,
    violationSummarize: true,
    voiceToWo: true,
  }),
  /** When true and `wo_classify` confidence ≥ threshold, the classifier
   *  applies its own suggestion. Even when auto-applied, the user can
   *  override afterwards. */
  autoApplyClassification: z.boolean().default(false),
  autoApplyClassificationThreshold: z
    .number()
    .min(0.5)
    .max(1)
    .default(DEFAULT_AUTO_APPLY_CLASSIFICATION_THRESHOLD),
  /** ISO 639-1 (`en`, `es`, `zh`, …). Whisper auto-detects when null,
   *  but a hint speeds up transcription noticeably for non-English. */
  defaultTranscriptionLanguage: z.string().min(2).max(8).default('en'),
  /** When true the runner stores full prompts in `ai_calls.metadata`
   *  rather than redacted summaries. Off by default — PII risk. */
  aiDebugMode: z.boolean().default(false),
});
export type OrgAiSettings = z.infer<typeof orgAiSettingsSchema>;

// ---------- Display ----------

/** Org-wide interface preferences. Column explanations start on so new
 *  users can hover a table heading to learn what it means. */
export const orgDisplaySettingsSchema = z.object({
  showColumnHints: z.boolean().default(true),
});
export type OrgDisplaySettings = z.infer<typeof orgDisplaySettingsSchema>;

export const DEFAULT_ORG_DISPLAY_SETTINGS: OrgDisplaySettings = {
  showColumnHints: true,
};

export const DEFAULT_ORG_AI_SETTINGS: OrgAiSettings = {
  enabled: true,
  features: {
    emailParse: true,
    woClassify: true,
    violationSummarize: true,
    voiceToWo: true,
  },
  autoApplyClassification: false,
  autoApplyClassificationThreshold: DEFAULT_AUTO_APPLY_CLASSIFICATION_THRESHOLD,
  defaultTranscriptionLanguage: 'en',
  aiDebugMode: false,
};

/** The full org-settings document (extended in later phases). */
export const orgSettingsSchema = z.object({
  workOrders: orgWorkOrderSettingsSchema.default(DEFAULT_ORG_WORK_ORDER_SETTINGS),
  violations: orgViolationSettingsSchema.default(DEFAULT_ORG_VIOLATION_SETTINGS),
  ai: orgAiSettingsSchema.default(DEFAULT_ORG_AI_SETTINGS),
  display: orgDisplaySettingsSchema.default(DEFAULT_ORG_DISPLAY_SETTINGS),
});
export type OrgSettings = z.infer<typeof orgSettingsSchema>;

export const DEFAULT_ORG_SETTINGS: OrgSettings = {
  workOrders: DEFAULT_ORG_WORK_ORDER_SETTINGS,
  violations: DEFAULT_ORG_VIOLATION_SETTINGS,
  ai: DEFAULT_ORG_AI_SETTINGS,
  display: DEFAULT_ORG_DISPLAY_SETTINGS,
};

/** PATCH body for the org-settings endpoint — every field optional. */
export const updateOrgSettingsInput = z
  .object({
    workOrders: orgWorkOrderSettingsSchema.partial().optional(),
    violations: orgViolationSettingsSchema.partial().optional(),
    ai: orgAiSettingsSchema.partial().optional(),
    display: orgDisplaySettingsSchema.partial().optional(),
  })
  .refine(
    (v) => Object.values(v).some((x) => x !== undefined),
    { message: 'Provide at least one section to update' },
  );
export type UpdateOrgSettingsInput = z.infer<typeof updateOrgSettingsInput>;

/**
 * Merge a partial input over an existing settings document, dropping
 * unknown keys via the Zod parser. Used by the API to compute the next
 * settings payload before the UPDATE.
 */
export function mergeOrgSettings(
  existing: OrgSettings,
  patch: UpdateOrgSettingsInput,
): OrgSettings {
  const merged: OrgSettings = {
    workOrders: { ...existing.workOrders, ...(patch.workOrders ?? {}) },
    violations: {
      ...existing.violations,
      ...(patch.violations ?? {}),
      // Nested objects need a manual merge because spread would replace
      // the whole sub-object with whatever the PATCH supplied.
      pollingIntervalOverrides: {
        ...existing.violations.pollingIntervalOverrides,
        ...(patch.violations?.pollingIntervalOverrides ?? {}),
      },
      quietHours: {
        ...existing.violations.quietHours,
        ...(patch.violations?.quietHours ?? {}),
      },
    },
    ai: {
      ...existing.ai,
      ...(patch.ai ?? {}),
      features: {
        ...existing.ai.features,
        ...(patch.ai?.features ?? {}),
      },
    },
    display: { ...existing.display, ...(patch.display ?? {}) },
  };
  return orgSettingsSchema.parse(merged);
}
